import type { Level } from "@/api/custom_types";
import { useGetUserWallet } from "@/services/users/queries";
import { useEffect, useRef, useState } from "react";

const LEVEL_ORDER: Level[] = ["BRONZE", "SILVER", "GOLD", "ELITE"];
const POLLING_INTERVAL = 30000;

interface UseBonusLevelPollingReturn {
	hasLevelUpgraded: boolean;
	currentLevel: Level | null | undefined;
	previousLevel: Level | null | undefined;
	bonusPercentage: number;
	dismissUpgrade: () => void;
}

export function useBonusLevelPolling(): UseBonusLevelPollingReturn {
	const [hasLevelUpgraded, setHasLevelUpgraded] = useState(false);
	const [previousLevel, setPreviousLevel] = useState<Level | null | undefined>(
		null,
	);
	const prevLevelRef = useRef<Level | null | undefined>(null);

	const { data: walletData } = useGetUserWallet({
		refetchInterval: POLLING_INTERVAL,
		refetchIntervalInBackground: false,
	});

	const currentLevel = walletData?.bonus?.level;
	const bonusPercentage = walletData?.bonus?.percentage || 0;

	useEffect(() => {
		if (!currentLevel) {
			return;
		}

		if (prevLevelRef.current === null || prevLevelRef.current === undefined) {
			prevLevelRef.current = currentLevel;
			return;
		}

		const prevIndex = LEVEL_ORDER.indexOf(prevLevelRef.current);
		const currentIndex = LEVEL_ORDER.indexOf(currentLevel);

		if (currentIndex > prevIndex && prevIndex >= 0) {
			setPreviousLevel(prevLevelRef.current);
			setHasLevelUpgraded(true);
		}

		prevLevelRef.current = currentLevel;
	}, [currentLevel]);

	const dismissUpgrade = () => {
		setHasLevelUpgraded(false);
	};

	return {
		hasLevelUpgraded,
		currentLevel,
		previousLevel,
		bonusPercentage,
		dismissUpgrade,
	};
}
